import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, CalendarDays, Clock } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { POSTS } from "../data/blog";
import useSeo from "../hooks/useSeo";
import { SITE_URL } from "../lib/site";

export default function Blog() {
  useSeo({
    title:
      "Blog — Startup Guides For Indian Founders | ZOZO Venture",
    description:
      "Practical, no-fluff guides for Indian founders — company registration, payment gateways, business banking, funding, and digital presence. Written by ZOZO Venture.",
    canonical: `${SITE_URL}/blog`,
    image: POSTS[0]?.cover,
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "Blog",
        name: "ZOZO Venture Blog — Startup Guides For Indian Founders",
        url: `${SITE_URL}/blog`,
        description:
          "Practical playbooks for Indian founders on company registration, startup funding, banking, payments, office space and digital presence.",
        publisher: {
          "@type": "Organization",
          name: "ZOZO Venture",
          url: `${SITE_URL}/`,
        },
        inLanguage: "en-IN",
        blogPost: POSTS.map((p) => ({
          "@type": "BlogPosting",
          headline: p.title,
          description: p.description,
          url: `${SITE_URL}/blog/${p.slug}`,
          datePublished: p.date,
          dateModified: p.date,
          image: p.cover,
          articleSection: p.tag,
          author: { "@type": "Organization", name: p.author },
        })),
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
          { "@type": "ListItem", position: 2, name: "Blog", item: `${SITE_URL}/blog` },
        ],
      },
    ],
  });

  return (
    <div className="min-h-screen bg-white text-[#0A0A0A] font-body" data-testid="blog-page">
      <Header />
      <main>
        <section className="border-b border-black/10 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 sm:pt-24 pb-12 sm:pb-16">
            <p className="text-xs font-semibold tracking-[0.22em] uppercase text-[#EAB308]">
              Founder&apos;s Guide · Blog
            </p>
            <h1 className="font-heading font-black text-5xl sm:text-6xl lg:text-7xl tracking-tight leading-[0.95] mt-4 max-w-4xl">
              Practical playbooks for India&apos;s new founders.
            </h1>
            <p className="mt-6 text-lg text-black/70 max-w-2xl">
              Company registration, banking, payments, funding, digital
              presence — the decisions that shape your next five years, broken
              down by people who&apos;ve handled them for 1,200+ founders.
            </p>
          </div>
        </section>

        <section className="bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {POSTS.map((p) => (
                <PostCard key={p.slug} post={p} />
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}

function PostCard({ post }) {
  return (
    <Link
      to={`/blog/${post.slug}`}
      data-testid={`blog-card-${post.slug}`}
      className="group block border border-black/80 bg-white transition-all duration-200 hover:-translate-y-1 hover:neo-shadow-sm"
    >
      <div className="relative aspect-[16/10] overflow-hidden border-b border-black/80">
        <img
          src={post.cover}
          alt={post.title}
          className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          loading="lazy"
        />
      </div>
      <div className="p-6">
        <div className="flex items-center gap-3 text-[11px] tracking-[0.18em] uppercase text-black/60">
          <span className="bg-[#EAB308] text-white px-2 py-0.5">{post.tag}</span>
          <span className="inline-flex items-center gap-1"><Clock size={12} />{post.readMinutes} min</span>
        </div>
        <h2 className="font-heading font-black text-xl sm:text-2xl mt-3 leading-tight">
          {post.title}
        </h2>
        <p className="mt-3 text-sm text-black/70 leading-relaxed line-clamp-3">
          {post.description}
        </p>
        <div className="mt-5 pt-4 border-t border-black/10 flex items-center justify-between text-xs text-black/60">
          <span className="inline-flex items-center gap-1.5"><CalendarDays size={12} />{formatDate(post.date)}</span>
          <span className="inline-flex items-center gap-1 text-[#EAB308] font-semibold">
            Read <ArrowUpRight size={14} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </span>
        </div>
      </div>
    </Link>
  );
}

function formatDate(d) {
  const dt = new Date(d);
  return dt.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
}
